
import legStart from '~/assets/map/leg_start.svg';
import legEnd from '~/assets/map/leg_end.svg';
import to from '~/utils/to';

export default {
  name: 'Map',
  data() {
    return {
      map: null,
      startIcon: null,
      endIcon: null,
      startLeg: null,
      endLeg: null,
      directionsService: null,
      directionsRenderer: null,
      latLng: {},
      loadingMap: true,
      googleMapLoaded: false,
      address: '',
    };
  },
  computed: {
    parkingProvider() {
      return this.$store.getters['route/getParkingProvider'];
    },
    merchantContent() {
      return this.$store.getters['route/getMerchantContent'];
    },
    getParkingService() {
      return this.$store.getters['route/getParkingService'];
    },
    airport() {
      return this.$store.state.airport;
    },
    airportName() {
      return this.$store.state.airport.name || '';
    },
    airportCity() {
      return this.$store.state.airport?.address?.city || '';
    },
  },
  watch: {
    parkingProvider(value) {
      this.address = `${value.country.name} ${value.cityName} ${value.address}`;
    },
    merchantContent(value) {
      /* eslint camelcase: "off" */
      if (value) {
        const {
          type,
          parking_type_address,
          parking_type_city_name,
          airport_address,
          airport_city_name,
          airport_country,
        } = value;

        let address = '';

        if (parking_type_address) {
          address = `${parking_type_address}, ${parking_type_city_name}, ${airport_country}`;
        }

        if (type === 'shuttle' && !address) {
          address = `${this.parkingProvider.address}, ${this.parkingProvider.cityName}, ${this.parkingProvider.country.name}`;
        }

        if (!address) {
          address = `${airport_address}, ${airport_city_name}, ${airport_country}`;
        }

        this.address = address;
      }
    },
    latLng(value) {
      this.setMapCenter(value);
    },
    getParkingService(value) {
      this.setZoom(value ? 15 : 13);
    },
    address(val) {
      this.setLatLng(val);
    },
    googleMapLoaded(val) {
      if (!val) return;

      if (this.address) this.setLatLng();

      if (this.latLng) this.setMapCenter(this.latLng);

      if (this.getParkingService) this.setZoom(this.getParkingService ? 15 : 13);
    },
  },
  created() {
    this.$nuxt.$on('calculate-display-route', this.calculateAndDisplayRoute);
  },
  mounted() {
    this.injectGoogleMapsScript();
    /* eslint-disable */
  },
  beforeDestroy() {
    this.$nuxt.$off('calculate-display-route', this.calculateAndDisplayRoute);
  },
  methods: {
    reloadPage() {
      window.location.reload();
    },
    injectGoogleMapsScript() {
      this.loadingMap = true;
      let googleMapApi = `https://maps.googleapis.com/maps/api/js?key=${this.$config.googleMapsAPIKey}&libraries=places`;
      const lang = this.$store.state.language.lang?.split('-');
      if (Array.isArray(lang)) {
        if (lang[0]) {
          googleMapApi += `&language=${lang[0].toLowerCase()}`;
        }
        if (lang[1]) {
          googleMapApi += `&region=${lang[1].toUpperCase()}`;
        }
      }

      const script = document.createElement('script');
      script.src = encodeURI(googleMapApi);

      script.onload = () => {
        this.loadMap();
        this.loadingMap = false;
        this.googleMapLoaded = true;
      };

      script.onerror = () => {
        this.loadingMap = false;
      };

      document.head.appendChild(script);
    },
    loadMap() {
      const googleMap = this.$refs.map;
      this.map = new google.maps.Map(googleMap, {
        zoom: 13,
        center: {
          lat: this.airport.address.latitude,
          lng: this.airport.address.longitude,
        },
      });
      this.directionsService = new google.maps.DirectionsService();
      this.directionsRenderer = new google.maps.DirectionsRenderer({ suppressMarkers: true });
      this.directionsRenderer.setMap(this.map);
      const sidebar = document.getElementById('sidebar');
      this.directionsRenderer.setPanel(sidebar);
      this.startIcon = new google.maps.MarkerImage(legStart);
      this.endIcon = new google.maps.MarkerImage(legEnd);
    },
    async setLatLng() {
      if (!this.googleMapLoaded) return;

      const { latitude: lat, longitude: lng } = this.parkingProvider;

      if (process.client && this.address) {
        this.latLng = await this.getAddressGeometryLocation(this.address);
      } else {
        this.latLng = { lat, lng };
      }
    },
    getAddressGeometryLocation(address) {
      const geocoder = new google.maps.Geocoder();

      return new Promise((resolve) => {
        geocoder.geocode({ address: address }, (results, status) => {
          if (status === google.maps.GeocoderStatus.OK) {
            const endLatLng = results[0].geometry.location;
            resolve({ lat: endLatLng.lat(), lng: endLatLng.lng() });
          } else {
            resolve({});
          }
        });
      });
    },
    setMapCenter(value) {
      if (!this.googleMapLoaded) return;

      this.map.setCenter(new google.maps.LatLng(value.lat, value.lng));
    },
    setZoom(value) {
      if (!this.googleMapLoaded) return;

      this.map.setZoom(value, `${this.airportCity} ${this.airportName}`);
    },
    async calculateAndDisplayRoute(value) {
      if (!this.googleMapLoaded) return;

      let params = {
        origin: {
          query: value.search,
        },
        destination: new google.maps.LatLng(this.latLng.lat, this.latLng.lng),
        travelMode: google.maps.TravelMode.DRIVING,
        unitSystem: google.maps.UnitSystem.METRIC,
      };

      const [err, response] = await to(this.directionsService.route(params));

      if (err) {
        setTimeout(() => window.alert(this.$i18n('customer.direction-not-found')), 1);
        return;
      }

      if (this.startLeg) {
        this.startLeg.setMap(null);
      }

      if (this.endLeg) {
        this.endLeg.setMap(null);
      }

      this.directionsRenderer.setDirections(response);

      const { legs = [] } = response.routes[0] || {};

      if (legs.length) {
        const { start_location, end_location } = legs[0];

        this.startLeg = new google.maps.Marker({
          position: new google.maps.LatLng(start_location.lat(), start_location.lng()),
          map: this.map,
          icon: this.startIcon,
        });

        this.endLeg = new google.maps.Marker({
          position: new google.maps.LatLng(end_location.lat(), end_location.lng()),
          map: this.map,
          icon: this.endIcon,
        });
      }
      this.$nuxt.$emit('active-results-list');
    },
  },
};
